import type { ScaleLinear } from 'd3-scale';
import { area, curveBumpX } from 'd3-shape';
import { animated, useSpring } from 'react-spring';
import { useMentiSpring } from '@mentimeter/motion/spring';
import type { GuessTheNumberData } from '../guess-the-number/GuessTheNumber';
import type { ScalesScore } from './Scales';

interface ScalesAreaProps {
  fill: string;
  scores: ScalesScore | GuessTheNumberData;
  xScale: ScaleLinear<number, number>;
  yScale: ScaleLinear<number, number>;
  showDistribution: boolean;
  paddingBottom?: number;
  disableAnimations?: boolean;
}

const AREA_OPACITY_DISTRIBUTION_HIDDEN = 0.32;
const AREA_OPACITY_DISTRIBUTION_SHOWING = 0.64;

export const ScalesArea = ({
  fill,
  scores,
  xScale,
  yScale,
  showDistribution,
  disableAnimations = false,
  paddingBottom = 0,
}: ScalesAreaProps) => {
  const scoresArray = Object.entries(scores)
    .map(([key, score]) => ({
      key: parseInt(key, 10),
      score,
    }))
    .sort((a, b) => a.key - b.key); // area generator expects sorted data

  const areaGenerator = area<{ score: number; key: number }>()
    .x((d) => xScale(d.key))
    .y1((d) => yScale(d.score))
    .y0(() => yScale(0))
    .curve(curveBumpX);

  const sameLengthArrayWithZeros = scoresArray.map((d) => ({
    key: d.key,
    score: 0,
  }));

  const pathStart = areaGenerator(sameLengthArrayWithZeros) || '';
  const path = areaGenerator(scoresArray) || '';

  const [styles] = useMentiSpring({
    from: { d: pathStart },
    to: { d: path },
    config: { tension: 280, friction: 30 },
    immediate: disableAnimations,
  });

  const fadeIn = useSpring({
    opacity: showDistribution
      ? AREA_OPACITY_DISTRIBUTION_SHOWING
      : AREA_OPACITY_DISTRIBUTION_HIDDEN,
    immediate: disableAnimations,
  });

  return (
    <g
      data-testid="scales-area"
      id="curve"
      style={{ transform: `translate(0, -${paddingBottom}px)` }}
    >
      <animated.path d={styles.d} fill={fill} opacity={fadeIn.opacity} />
    </g>
  );
};
